import React, { useEffect, useLayoutEffect } from "react";
import {} from "prop-types";
import { Button, ButtonGroup, Form, Stack } from "react-bootstrap";
import styles from "./styles/CodeValidate.module.css";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { APP_TITLE_SUFFIX } from "@constants";
import { InputField } from "@components";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  codeValidatorAction,
  confirmValidatorAction,
  registerAction,
} from "@redux/actions";
import { isNull } from "lodash";

const validateSchema = Yup.object().shape({
  codeNumber: Yup.string()
    .required("Verification Code is required")
    .matches(/^[0-9]{4}$/, "Invalid Verification Code format"),
});

const initialValues = { codeNumber: "" };
const CodeValidate = () => {
  // state

  // variables
  const inputFields = [
    {
      name: "codeNumber",
      type: "text",
      label: "Verification Code",
      placeholder: "Enter the verification code",
    },
  ];

  // logics before hooks
  const dispatch = useDispatch();
  const handleRegister = (values) => {
    const { isInstructor, categoryId, email, codeNumber, ...rest } = values;
    dispatch(
      confirmValidatorAction({
        data: [email, codeNumber],
        onSuccess: () => {
          dispatch(
            registerAction(
              isInstructor
                ? { ...state, categoryId, ...rest }
                : { ...state, ...rest }
            )
          );
        },
      })
    );
  };

  // hooks
  const { register, confirmValidator } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldError,
    values,
    errors,
    touched,
  } = useFormik({
    initialValues: { ...state, codeNumber: "" } || initialValues,
    validationSchema: validateSchema,
    onSubmit: handleRegister,
  });

  // logics
  const handleResendCode = () => {
    dispatch(codeValidatorAction({ data: [values.email, "Register"] }));
  };

  const handleReturn = () => {
    navigate("/sign-up-step", { state });
  };

  // UI partials
  const validateForm = (
    <Form noValidate onSubmit={handleSubmit}>
      <Stack gap={4}>
        {inputFields.map((field, index) => (
          <Form.Group key={index} controlId={field.name}>
            <InputField
              required
              name={field.name}
              type={field.type}
              label={field.label}
              placeholder={field.placeholder}
              value={values[field.name]}
              onChange={handleChange}
              onBlur={handleBlur}
              touchedValue={touched[field.name]}
              errorsValue={errors[field.name]}
            />
          </Form.Group>
        ))}
        <div className="text-left">
          Not received the code?{" "}
          <a className={styles.link} onClick={handleResendCode}>
            Resend code
          </a>
        </div>
        <ButtonGroup>
          <Button variant="light" type="button" onClick={handleReturn}>
            <i className="bi bi-arrow-left mr-2" />
            Previous Step
          </Button>
          <Button type="submit">Register</Button>
        </ButtonGroup>
      </Stack>
    </Form>
  );

  // effects
  useEffect(() => {
    if (!register.processing && register.data?.isSuccess) {
      navigate("/sign-in", { state: { email: state.email } });
    }
  }, [register.processing]);

  useEffect(() => {
    if (
      !isNull(confirmValidator.success) &&
      !confirmValidator.processing &&
      !confirmValidator.success
    ) {
      setFieldError(
        "codeNumber",
        "Verification Code is not correct or expired"
      );
    }
  }, [confirmValidator.processing]);

  useLayoutEffect(() => {
    document.title = `Sign up - 3/3 | ${APP_TITLE_SUFFIX}`;
  }, []);

  // render
  if (!state) return <Navigate to={"/sign-up-step"} />;
  return (
    <Stack gap={4} className={styles.container}>
      <Stack>
        <h2>
          <b>Verify Your Email</b>
        </h2>
        <span>
          Please enter verification code that we have sent to your email.
        </span>
      </Stack>
      {validateForm}
      <div>
        Already have an account? <Link to={"/sign-in"}>Sign In</Link>
      </div>
    </Stack>
  );
};

/* required: display name */
CodeValidate.displayName = "CodeValidate";

/* required: props types */
CodeValidate.propTypes = {};

/* required: default props */
CodeValidate.defaultProps = {};

export { CodeValidate };
